
import React, { useRef } from "react";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";

interface BannerUploadProps {
  bannerPreview: string | null;
  onBannerChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveBanner: () => void;
}

const BannerUpload = ({ bannerPreview, onBannerChange, onRemoveBanner }: BannerUploadProps) => {
  // Create a reference to the file input element
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Handle button click to trigger the file input
  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };
  
  return (
    <div className="space-y-4 border-t pt-6">
      <h3 className="text-lg font-medium">Banner Image</h3>
      <p className="text-sm text-redflags-gray">
        Your banner will appear at the top of your company profile page.
        <span className="font-medium block mt-1">Recommended: 1128 × 191 pixels</span>
      </p>
      
      <div className="flex flex-col gap-4">
        <div className="border rounded-lg p-4 w-full h-40 flex items-center justify-center bg-white overflow-hidden">
          {bannerPreview ? (
            <img 
              src={bannerPreview} 
              alt="Company banner" 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="flex flex-col items-center justify-center text-gray-300">
              <Upload className="w-12 h-12 mb-2" />
              <p className="text-sm">No banner image uploaded</p>
            </div>
          )}
        </div>
        
        <div className="flex space-x-2">
          <Button 
            variant="outline" 
            type="button" 
            className="flex items-center gap-2"
            onClick={handleButtonClick}
          >
            <Upload className="w-4 h-4" />
            {bannerPreview ? 'Change banner' : 'Upload banner'}
          </Button>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onBannerChange}
          />
          
          {bannerPreview && (
            <Button 
              variant="outline" 
              type="button" 
              onClick={onRemoveBanner}
              className="text-redflags-red"
            >
              Remove
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BannerUpload;
